import { autoinject, transient, observable, computedFrom, useView } from "aurelia-framework";
import { GhgVerificationRatingFactorClientModel, GhgVerificationClientModel, GhgVerificationStatusEnum } from "../../../../../../models/ghgs/ghg.models";
import { GhgService } from "../../../../../../services/ghg/ghg.service";
import * as Enumerable from "linq";
import { MenuItem } from "../../../../../components/menu/menu-item";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipDialogServiceConstants } from "../../../../../../constants/basics/rightship-dialog.constants";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { NotificationClientService, NotificationType } from "../../../../../../services/basics/notification-client.service";
import { UserClientService } from "../../../../../../services/authentication/user-client.service";
import { Assignment } from "../../../../../components/assignment/assignment";
import { GhgFactorsRenderer } from "../../helpers/ghg-factors-renderer";
import { CloneHelper } from "@rightship-tech/common-ui/helpers/clone.helper";
import { RightshipEntityAttachmentManager } from "../../../common-steps/rightship-entity-attachment/rightship-entity-attachment-manager";
import { AttachmentsTypeGuids } from "../../../../../../constants/modules/attachments-type-guids";

@autoinject()
@transient()
@useView('views/pages/modules/ghg/steps/ghg-rating-factors/ghg-rating-factors.html')
export class GhgRatingFactors {

    public factorsByGroup: { [group: string]: GhgVerificationRatingFactorClientModel[] } = {};
    public allFactors: GhgVerificationRatingFactorClientModel[] = [];
    public originalFactors: GhgVerificationRatingFactorClientModel[];
    public groups: string[] = [];
    public ghgVerification: GhgVerificationClientModel;
    public someSelected: boolean;
    public assignment: Assignment;
    public ghgFactorsRenderer: GhgFactorsRenderer;
    public customerId: number;
    public originalSnapshot: string;
    public rightshipEntityAttachmentManager: RightshipEntityAttachmentManager;

    @computedFrom('ghgVerification.isClosed', 'customerId', 'ghgVerification.assignedUserId')
    public get canSave() {
        return this.ghgVerification && !this.ghgVerification.isClosed && !this.customerId && this.ghgVerification.assignedUserId == this.userClientService.currentUser.id;
    }
    
    @computedFrom('rightshipEntityAttachmentManager.items', 'rightshipEntityAttachmentManager.items.length')
    public get documents() {
        const types = [
            AttachmentsTypeGuids.EvdiTechnicalFile.toLowerCase(),
            AttachmentsTypeGuids.CapacityPlan.toLowerCase(),
            AttachmentsTypeGuids.CurrentClassCertificate.toLowerCase(),
            AttachmentsTypeGuids.MainEngineShopTestFile.toLowerCase(),
            AttachmentsTypeGuids.AuxiliaryEngineShopTestFile.toLowerCase(),
            AttachmentsTypeGuids.SeaTrialReport.toLowerCase(),
            AttachmentsTypeGuids.ReLiquefactionDocumentation.toLowerCase(),
            AttachmentsTypeGuids.CertifiedDocument.toLowerCase()
        ]
        return this.rightshipEntityAttachmentManager.items.filter(x => types.some(y => x.attachmentTypeGuid && y == x.attachmentTypeGuid.toLowerCase()));
    }

    @observable()
    selectAll: boolean;

    constructor(private ghgService: GhgService,
        private dialogClientService: DialogClientService,
        private notificationClientService: NotificationClientService,
        private userClientService: UserClientService) {
        this.customerId = userClientService.currentUser.settings.customerId;
    }

    public activate(model: { ghgVerification: GhgVerificationClientModel, assignment: Assignment, rightshipEntityAttachmentManager: RightshipEntityAttachmentManager }) {

        if (!model.ghgVerification) {
            return;
        }

        this.assignment = model.assignment;
        this.ghgVerification = model.ghgVerification;
        this.rightshipEntityAttachmentManager = model.rightshipEntityAttachmentManager;
        this.reload(model.ghgVerification.ghgVerificationId);
    }

    public reload(ghgVerificationId: number) {
        this.ghgService.getGhgVerificationRatingFactorsClientModel(ghgVerificationId).then(factors => {

            const factorsDic: { [group: string]: GhgVerificationRatingFactorClientModel[] } = {};

            this.ghgFactorsRenderer = new GhgFactorsRenderer(factors, this.ghgVerification);

            this.groups = Enumerable.from(factors).select(x => x.group).distinct().toArray();
            this.groups.forEach(group => {
                factorsDic[group] = [];
            });

            factors.forEach(factor => {
                factorsDic[factor.group].push(factor);
            });

            this.factorsByGroup = factorsDic;
            this.allFactors = factors;
            this.originalFactors = CloneHelper.cloneObject(factors);
            this.originalSnapshot = JSON.stringify(factors.map(x => (
                {
                    passed: x.passed,
                    ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                    revisedValue: x.revisedValue
                })));
        });
    }

    public menuHasItems(factor: GhgVerificationRatingFactorClientModel): boolean {
        return this.getMenuItems(factor).filter(x => x.isVisible).length > 0;
    }

    public getMenuItems(factor: GhgVerificationRatingFactorClientModel): MenuItem[] {
        return [
            {
                click: () => this.showComments(factor),
                isVisible: true,
                item: {
                    text: 'Comments'
                },
                classList: '',
            },
            {
                click: () => this.showHistory(factor),
                isVisible: factor.vesselProperty != null,
                item: {
                    text: 'Show History'
                },
                classList: '',
            }
        ];
    }

    onSelectedFactor() {
        this.someSelected = this.allFactors.some(x => x.isSelected);
    }

    selectAllChanged() {
        this.allFactors.forEach(factor => {
            factor.isSelected = this.selectAll;
        });
        this.someSelected = this.allFactors.some(x => x.isSelected);
    }

    setSelectedAsPassed() {
        this.allFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = true;
            }
        });
        this.selectAll = false;
    }

    setSelectedAsFailed() {
        this.allFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = false;
            }
        });
        this.selectAll = false;
    }

    setSelectedAsNotApplicable() {
        this.allFactors.forEach(factor => {
            if (factor.isSelected) {
                factor.passed = null;
            }
        });
        this.selectAll = false;
    }

    hasChanges() {
        return this.canSave && this.originalSnapshot && this.originalSnapshot != JSON.stringify(this.allFactors.map(x => (
            {
                passed: x.passed,
                ghgRatingFactorSourceId: x.ghgRatingFactorSourceId,
                revisedValue: x.revisedValue
            })));
    }

    public discardChanges() {
        this.allFactors = CloneHelper.cloneObject(this.originalFactors);
    }

    save = () => {
        return new Promise<void>((resolve, reject) => {
            if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
                this.ghgService.saveGhgVerificationRatingFactors({
                    factors: this.allFactors,
                    ghgVerificationId: this.ghgVerification.ghgVerificationId
                }).then(() => {
                    this.notificationClientService.show("Rating Factors saved successfully", "", NotificationType.Success);
                    this.reload(this.ghgVerification.ghgVerificationId);
                    resolve();
                }).catch(() => {
                    this.notificationClientService.show("Failed to save Rating Factors", "", NotificationType.Error);
                    reject();
                });
            } else {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                    name: "Environmental Review Request",
                    submit: this.assignToMe,
                    callback: () => {
                        this.save().then(() => resolve()).catch(() => reject());
                    },
                    onCancel: () => reject(),
                    currentAssignment: this.assignment.assignedEntity.assignedToUserName
                }, true, false, false);
            }
        });
    }

    assignToMe = () => {
        return new Promise<void>((resolve) => {
            if (this.assignment) {
                this.assignment.assignToMe().then(() => {
                    this.ghgVerification.ghgVerificationStatusId = GhgVerificationStatusEnum.InProgress;
                    this.ghgVerification.assignedUserId = this.userClientService.currentUser.id;
                    resolve();
                });
            } else {
                resolve();
            }
        });
    }

    showHistory(factor: GhgVerificationRatingFactorClientModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.FullHistoryAttributeChangesDialog, {
            entityId: this.ghgVerification.vesselId,
            entityTypeId: EntityTypes.Vessel,
            propertyName: factor.vesselProperty,
            decimalPlaces: 2,
            dataType: factor.dataType
        }, true, false, false, 'right');
    }

    showComments(factor: GhgVerificationRatingFactorClientModel) {
        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgCommentsDialog, { factor, ghgVerification: this.ghgVerification }, true, false, false, 'right');
    }

    updateOutcome(value: boolean, factor: GhgVerificationRatingFactorClientModel) {
        if (this.ghgVerification.assignedUserId == this.userClientService.currentUser.id) {
            if (!this.canSave || !factor)
                return;

            if (factor) {
                factor.passed = value;
            }
        } else {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.GhgVerificationAssignmentDialog, {
                name: "Environmental Review Request",
                submit: this.assignToMe,
                callback: () => {
                    this.updateOutcome(value, factor);
                },
                currentAssignment: this.assignment?.assignedEntity?.assignedToUserName
            }, true, false, false);
        }
    }
}
